"use client";

import React, { useState, useEffect } from "react";
import { useRefinanceCalculator } from "@/hooks/useRefinanceCalculator";

import SectionHeader from "./SectionHeader";
import BarComparisonChart from "./charts/BarComparisonChart";
import BreakEvenLineChart from "./charts/BreakEvenLineChart";

import CurrentLoanTable from "./CurrentLoanTable";

import RefiLoanTable from "./RefiLoanTable";

type RefinanceResultProps = {
    /** optional money formatter */
    toCurrency?: (n: number) => string;
};

const tabs = ["PAYMENTS", "BREAK EVEN", "CURR LOAN", "REFI LOAN"];

const RefinanceResult = ({
    toCurrency = (n) =>
        `$${n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`,
}: RefinanceResultProps) => {
    const [activeTab, setActiveTab] = useState("PAYMENTS");
    const [headerOpen, setHeaderOpen] = useState(false);

    /* pull numbers from the hook (the ClearPath logic) */
    const { currentMonthlyPayment, currentInterest, refiMonthlyPayment, refiInterest, comparisonChartData } =
        useRefinanceCalculator();

    // Temporary break-even data based on 1-year projection (adjust as needed)
    const breakEvenData = [
        { x: 0, currentLoan: 0, refiLoan: 0 },
        { x: 1, currentLoan: currentMonthlyPayment * 12, refiLoan: refiMonthlyPayment * 12 },
    ];

    const monthlySavings = currentMonthlyPayment - refiMonthlyPayment;

    useEffect(() => {
        if (activeTab === "PAYMENTS" && monthlySavings > 0) {
            setHeaderOpen(true);
        }
    }, [monthlySavings, activeTab]);

    return (
        <div className="w-full border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            <SectionHeader
                title="Refinancing may save you"
                subtitle={` ${toCurrency(monthlySavings)} / MO`}
                isOpen={headerOpen}
                setIsOpen={setHeaderOpen}
            />

            {/* Only show content if the section is open */}
            {headerOpen && (
                <>
                    {/* Table Summary */}
                    <table className="w-full text-sm text-right border border-gray-300 shadow rounded bg-white 2xl:w-[600px] 2xl:m-auto 2xl:mt-3">
                        <thead className="bg-gray-300 2xl:mt-2">
                            <tr className="bg-blue text-white  text-[15px]">
                                <th className="px-2 py-1 border-l border-[#dddddd]">* </th>
                                <th className="px-2 py-1 border-l border-[#dddddd]">Current Loan</th>
                                <th className="px-2 py-1 border-l border-[#dddddd]">Refi Loan</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="bg-[#f6f9ff]  border-t">
                                <td className="px-2 py-1 text-[15px] font-black  border-l border-[#dddddd]">
                                    Monthly Payment:
                                </td>
                                <td className="px-2 py-1 text-[22px] text-blue font-bold  border-l border-[#dddddd]">
                                    {toCurrency(currentMonthlyPayment)}
                                </td>
                                <td className="px-2 py-1 text-[22px] text-blue font-bold  border-l border-[#dddddd]">
                                    {toCurrency(refiMonthlyPayment)}
                                </td>
                            </tr>
                            <tr className="bg-white border-t">
                                <td className="px-2 py-1 text-[15px] font-black  border-l border-[#dddddd]">
                                    Total Interest Paid:
                                </td>
                                <td className="px-2 py-1 text-[22px] text-blue font-bold  border-l border-[#dddddd]">
                                    {toCurrency(currentInterest)}
                                </td>
                                <td className="px-2 py-1 text-[22px] text-blue font-bold  border-l border-[#dddddd]">
                                    {toCurrency(refiInterest)}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <h2 className="text-[24px] w-[874px] text-darkBlue font-bold text-center 2xl:mt-8 uppercase">
                        Refinancing may save you {toCurrency(monthlySavings)} / MO
                    </h2>

                    {/* Tabs & charts */}
                    <div className="mt-8 ">
                        <div className="flex space-x-2 pb-2 mb-4 uppercase  justify-center mt-4 px-4">
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => {
                                        setActiveTab(tab);
                                        if (tab === "PAYMENTS") setHeaderOpen(true);
                                    }}
                                    className={`px-4 py-2 font-bold 2xl:w-[180px] uppercase text-white active:bg-blue ${
                                        activeTab === tab
                                            ? "bg-blue border-blue"
                                            : "bg-darkBlue border-transparent hover:border-[#1275b9] hover:bg-blue focus:bg-blue"
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {activeTab === "PAYMENTS" && <BarComparisonChart data={comparisonChartData} />}
                        {activeTab === "BREAK EVEN" && <BreakEvenLineChart data={breakEvenData} />}
                        {activeTab === "CURR LOAN" && <CurrentLoanTable />}
                        {activeTab === "REFI LOAN" && <RefiLoanTable />}
                    </div>
                </>
            )}
        </div>
    );
};

export default RefinanceResult;
